import type { ConsoleEntryFormatter, EmittableLogLevel, LogEntry } from '@packages/core/logging';

const BADGE_BASE = 'border-radius:3px;padding:1px 5px;font-weight:600;';

/** Per-level `%c` badge styles — a visual attention hierarchy, dim to loud. */
const LEVEL_BADGE_STYLE: Readonly<Record<EmittableLogLevel, string>> = {
    trace: `${BADGE_BASE}background:#eceff1;color:#90a4ae;`,
    debug: `${BADGE_BASE}background:#eceff1;color:#607d8b;`,
    info: `${BADGE_BASE}background:#e3f2fd;color:#1565c0;`,
    notice: `${BADGE_BASE}background:#1565c0;color:#ffffff;`,
    warn: `${BADGE_BASE}background:#fff3e0;color:#e65100;`,
    error: `${BADGE_BASE}background:#ffebee;color:#c62828;`,
    critical: `${BADGE_BASE}background:#c62828;color:#ffffff;`,
    alert: `${BADGE_BASE}background:#b71c1c;color:#ffffff;`,
    emergency: `${BADGE_BASE}background:#7f0000;color:#ffffff;text-transform:uppercase;`,
};

/** Per-level message text styles — sub-info noise recedes, notice stands out. */
const LEVEL_MESSAGE_STYLE: Readonly<Record<EmittableLogLevel, string>> = {
    trace: 'color:#b0bec5;',
    debug: 'color:#90a4ae;',
    info: '',
    notice: 'font-weight:600;',
    warn: '',
    error: '',
    critical: 'font-weight:600;',
    alert: 'font-weight:600;',
    emergency: 'font-weight:600;',
};

/**
 * Chrome `%c` badge formatter: colored level badges, dim-to-loud attention
 * hierarchy. Web-only — React Native's console has no `%c` support.
 */
export class BadgeConsoleFormatter implements ConsoleEntryFormatter {
    format(entry: LogEntry): readonly unknown[] {
        const level = entry.level;
        const prefix = entry.name === '' ? entry.message : `[${entry.name}] ${entry.message}`;
        const styledMessage = `%c${entry.level}%c ${prefix}`;
        const styles = [LEVEL_BADGE_STYLE[level], LEVEL_MESSAGE_STYLE[level]];

        return Object.keys(entry.meta).length > 0 ? [styledMessage, ...styles, entry.meta] : [styledMessage, ...styles];
    }
}
